import React from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { FaPrint, FaGlobe, FaCity, FaProjectDiagram, FaUserTie, FaChartBar, FaTable, FaBuilding, FaSearch, FaChartPie } from 'react-icons/fa';
import { SEC_LABELS, C } from '../utils/constants';

const GROUPBY_OPTIONS = [
  { value: 'general',    label: 'Général',        icon: <FaGlobe size={12} />,        desc: 'Un rapport global' },
  { value: 'ville',      label: 'Par ville',      icon: <FaCity size={12} />,         desc: 'Un bloc par ville' },
  { value: 'projet',     label: 'Par projet',     icon: <FaProjectDiagram size={12} />, desc: 'Un bloc par projet' },
  { value: 'commercial', label: 'Par commercial', icon: <FaUserTie size={12} />,      desc: 'Un bloc par commercial' },
];

const SECTION_CATEGORIES = [
  { id: 'synthese',   label: 'Synthèse',           icon: <FaChartPie size={11} />,  sections: ['kpi', 'projets', 'commerciaux'] },
  { id: 'graphiques', label: 'Graphiques',          icon: <FaChartBar size={11} />,  sections: ['ca-projet', 'graphiques'] },
  { id: 'analyses',   label: 'Analyses',            icon: <FaSearch size={11} />,    sections: ['analyse-rec', 'synthese', 'duree-traitement'] },
  { id: 'notaire',    label: 'Dossiers Notaire',    icon: <FaBuilding size={11} />,  sections: ['notaire-soon'] },
  { id: 'details',    label: 'Tableaux détaillés',  icon: <FaTable size={11} />,     sections: ['tableau', 'detail-ventes', 'detail-rec', 'detail-pn'] },
];

const ALL_SECTIONS    = SECTION_CATEGORIES.flatMap(c => c.sections);
const SECTIONS_GROUPED    = ['kpi', 'graphiques', 'tableau', 'detail-ventes', 'detail-rec', 'detail-pn'];
const SECTIONS_COMMERCIAL = ['kpi', 'graphiques', 'detail-ventes'];
const SECTIONS_FOR = { general: ALL_SECTIONS, ville: SECTIONS_GROUPED, projet: SECTIONS_GROUPED, commercial: SECTIONS_COMMERCIAL };

const PrintModal = ({
  show, onHide,
  sections, onToggle, onToggleGroup, onSelectAll, onSelectNone,
  title, onTitleChange,
  groupBy, onGroupByChange,
  orientation, onOrientationChange,
  onPrint,
}) => {
  const availableSections = SECTIONS_FOR[groupBy] || ALL_SECTIONS;

  return (
    <Modal show={show} onHide={onHide} size="md" centered>
      <Modal.Header closeButton style={{ background: C.light, borderBottom: `1px solid ${C.border}` }}>
        <Modal.Title style={{ fontSize: '0.98rem', fontWeight: 700, color: C.primary, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaPrint size={14} /> Configurer le rapport PDF
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '18px 20px', maxHeight: '72vh', overflowY: 'auto' }}>

        {/* Titre */}
        <Form.Group className="mb-3">
          <Form.Label style={labelStyle}>Titre du rapport</Form.Label>
          <Form.Control
            type="text" value={title} onChange={e => onTitleChange(e.target.value)}
            placeholder="Rapport Décisionnel Almadina" size="sm"
            style={{ borderColor: C.border, borderRadius: 7, fontSize: '0.85rem' }}
          />
        </Form.Group>

        {/* Découpage + Orientation côte à côte */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 18 }}>

          {/* Découpage */}
          <div>
            <Form.Label style={labelStyle}>Découpage</Form.Label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {GROUPBY_OPTIONS.map(opt => {
                const active = groupBy === opt.value;
                return (
                  <button key={opt.value} onClick={() => onGroupByChange(opt.value)}
                    style={{ padding: '7px 5px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${active ? C.primary : C.border}`, background: active ? C.greenBg : '#fafafa', color: active ? C.primary : '#555', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, transition: 'all 0.15s' }}>
                    <span style={{ color: active ? C.primary : '#aaa' }}>{opt.icon}</span>
                    <span style={{ fontSize: '0.7rem', fontWeight: 700 }}>{opt.label}</span>
                    <span style={{ fontSize: '0.58rem', color: '#aaa', textAlign: 'center' }}>{opt.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Orientation */}
          <div>
            <Form.Label style={labelStyle}>Orientation</Form.Label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { value: 'landscape', label: 'Paysage (A4)',  desc: 'Recommandé — tableaux larges' },
                { value: 'portrait',  label: 'Portrait (A4)', desc: 'Idéal pour les KPI seuls' },
              ].map(opt => {
                const active = orientation === opt.value;
                return (
                  <button key={opt.value} onClick={() => onOrientationChange(opt.value)}
                    style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', border: `2px solid ${active ? C.primary : C.border}`, background: active ? C.greenBg : '#fafafa', color: active ? C.primary : '#555', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, transition: 'all 0.15s', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 700 }}>{opt.label}</span>
                    <span style={{ fontSize: '0.6rem', color: '#aaa' }}>{opt.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* En-tête sections */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <Form.Label style={{ ...labelStyle, marginBottom: 0 }}>Sections à inclure</Form.Label>
          <span style={{ flex: 1 }} />
          <button onClick={() => onSelectAll(availableSections)} style={linkBtn(C.primary)}>Tout</button>
          <span style={{ color: '#ddd', margin: '0 6px' }}>|</span>
          <button onClick={onSelectNone} style={linkBtn('#c62828')}>Aucun</button>
        </div>

        {/* Catégories */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SECTION_CATEGORIES.map(cat => {
            const catSecs = cat.sections.filter(id => availableSections.includes(id));
            if (catSecs.length === 0) return null;
            const allIn  = catSecs.every(id => sections.has(id));
            const someIn = catSecs.some(id => sections.has(id));
            return (
              <div key={cat.id} style={{ border: `1px solid ${someIn ? C.border : '#eee'}`, borderRadius: 8, overflow: 'hidden' }}>
                {/* En-tête catégorie */}
                <div style={{ background: someIn ? '#fdf8f4' : '#fafafa', padding: '7px 12px', display: 'flex', alignItems: 'center', gap: 7, borderBottom: `1px solid ${someIn ? C.border : '#eee'}` }}>
                  <span style={{ color: someIn ? C.primary : '#bbb' }}>{cat.icon}</span>
                  <span style={{ fontSize: '0.73rem', fontWeight: 700, color: someIn ? '#444' : '#aaa', flex: 1 }}>{cat.label}</span>
                  <span style={{ fontSize: '0.62rem', color: '#bbb', marginRight: 6 }}>
                    {catSecs.filter(id => sections.has(id)).length}/{catSecs.length}
                  </span>
                  <button
                    onClick={() => onToggleGroup(catSecs)}
                    style={{ fontSize: '0.65rem', color: allIn ? '#c62828' : C.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 700 }}>
                    {allIn ? 'Aucun' : 'Tout'}
                  </button>
                </div>
                {/* Items */}
                <div style={{ padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {catSecs.map(id => (
                    <label key={id}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.8rem', color: '#333', padding: '3px 6px', borderRadius: 6, background: sections.has(id) ? C.greenBg : 'transparent', transition: 'background 0.1s' }}>
                      <input type="checkbox" checked={sections.has(id)} onChange={() => onToggle(id)}
                        style={{ cursor: 'pointer', accentColor: C.primary, width: 13, height: 13 }} />
                      {SEC_LABELS[id]}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

      </Modal.Body>

      <Modal.Footer style={{ background: '#f9fbf9', borderTop: `1px solid ${C.border}`, gap: 8 }}>
        <div style={{ flex: 1, fontSize: '0.68rem', color: '#aaa' }}>
          {sections.size} section{sections.size !== 1 ? 's' : ''} · {orientation === 'landscape' ? 'Paysage' : 'Portrait'}
        </div>
        <Button variant="light" size="sm" onClick={onHide} style={{ borderColor: '#ccc', fontSize: '0.8rem' }}>
          Annuler
        </Button>
        <Button size="sm" onClick={onPrint} disabled={sections.size === 0}
          style={{ background: C.primary, border: 'none', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FaPrint size={12} /> Imprimer / Enregistrer PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const labelStyle = { fontSize: '0.75rem', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4, display: 'block', marginBottom: 8 };
const linkBtn = color => ({ fontSize: '0.7rem', color, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 });

export default PrintModal;
